import type { Dirent } from 'node:fs';
import fs from 'node:fs/promises';
import os from 'node:os';
import path from 'node:path';

import { claudeProjectsRoot, encodeCwdToDir } from '@lore/transcript-locate';

import { CLIError, isErrorOfType } from '../../lib/errors.ts';
import {
  type ClaudeHistoryEntry,
  claudeHistoryFilePath,
  parseAllHistoryEntries,
} from '../../lib/harnesses/claudeCode.ts';
import { log } from '../../lib/logger.ts';
import { emptyUploadFilterConfig, evaluateUploadFilterSession } from '../../lib/uploadFilters.ts';
import { copyFileSnapshot, watchedFileStateForPath } from '../files.ts';
import { findGitRoot, repoOriginPathForProject } from '../repo.ts';
import { readTranscriptCwd } from './claudeTranscriptScan.ts';
import { collectHistoricalCandidatesByKey, prepareHistoricalCandidateCollector } from './historicalCollect.ts';
import type {
  BuildPendingTranscriptWatchesOptions,
  CollectLocalFilesOptions,
  CollectLocalFilesResult,
  HarnessAdapter,
  LocalThreadFile,
  PendingTranscriptWatch,
  WatchedFileState,
} from './types.ts';

const PENDING_WATCH_QUIET_PERIOD_MS = 60 * 60 * 1000;

// Only transcripts modified within this window are scanned. The window — not
// the per-harness floor — is the enumeration bound: a floor advanced to the
// max handled mtime would sit above a concurrently-active, lower-mtime session
// and miss its growth. Per-session cursors dedup within the window. Growth
// bumps mtime, so a resumed older session re-enters the window. (TAN-4539)
const RECENCY_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

const missingSessionID = Symbol('missingSessionID');

type ClaudeTranscriptEntry = {
  type?: unknown;
  message?: {
    content?: unknown;
  };
};

type SessionPathResult = {
  fullPath: string;
  relativeFilename: string;
  key: string;
};

export const claudeAdapter: HarnessAdapter<ClaudeHistoryEntry> = {
  name: 'claudeCode',
  // Watch the projects dir directly (recursively, via watchLoop) so transcript
  // growth fires a sync — history.jsonl appends are too sparse. (TAN-4539)
  historyFilePath: claudeProjectsDir,
  readUnuploadedEntries,
  readHistoricalEntries: (floor) => readClaudeEntries(floor, true),
  entryFileKey: async (entry) => {
    const resolved = await claudeSessionJSONLPath(entry);
    return resolved === missingSessionID
      ? `__missing_session__:${entry.project}:${entry.timestamp}`
      : resolved.key;
  },
  collectLocalFiles,
  collectHistoricalCandidates: (candidates, options) => collectHistoricalCandidatesByKey(claudeAdapter, candidates, options),
  prepareHistoricalCollector: () => prepareHistoricalCandidateCollector(claudeAdapter),
  buildPendingTranscriptWatches,
};

// Enumerate Claude Code sessions by scanning the transcripts on disk rather
// than reading history.jsonl entry timestamps (which only advance on certain
// prompt events, so a growing transcript can be invisible there). The coarse
// A zero floor uses RECENCY_WINDOW_MS; an explicit floor is honored for
// historical scans. cwd is resolved lazily in collectLocalFiles, after the
// cheap per-session cursor check, so live upload never reads a transcript it
// will skip. (TAN-4539)
export async function readUnuploadedEntries(
  floor: number,
): Promise<ClaudeHistoryEntry[]> {
  return (await readClaudeEntries(floor, false)).entries;
}

async function readClaudeEntries(
  floor: number,
  countWarnings: boolean,
): Promise<{ entries: ClaudeHistoryEntry[]; warningCount: number }> {
  const projectsDir = claudeProjectsDir();
  // A zero floor is the ordinary, uninitialised live scan and retains the
  // bounded 14-day default. Any explicit floor is authoritative, including a
  // historical threshold older than that window. Negative infinity is used by
  // inventory to request all history without inventing a second scanner.
  const cutoff = floor === 0 ? Date.now() - RECENCY_WINDOW_MS : floor;

  let projectDirents: Dirent[];
  try {
    projectDirents = await fs.readdir(projectsDir, { withFileTypes: true });
  } catch (error) {
    if (isErrorOfType(error, 'ENOENT')) return { entries: [], warningCount: 0 };
    throw new Error(`readdir ${projectsDir}: ${String(error)}`);
  }

  const entries: ClaudeHistoryEntry[] = [];
  let warningCount = 0;
  let historyIndex: Map<string, string> | null = null;
  const getHistoryIndex = async (): Promise<Map<string, string>> => {
    historyIndex ??= await buildHistoryCwdIndex();
    return historyIndex;
  };
  for (const projectDirent of projectDirents) {
    if (!projectDirent.isDirectory()) continue;
    const projectDir = path.join(projectsDir, projectDirent.name);

    let fileDirents: Dirent[];
    try {
      fileDirents = await fs.readdir(projectDir, { withFileTypes: true });
    } catch {
      if (countWarnings) warningCount += 1;
      continue;
    }

    for (const fileDirent of fileDirents) {
      if (!fileDirent.isFile() || !fileDirent.name.endsWith('.jsonl')) continue;
      const fullPath = path.join(projectDir, fileDirent.name);

      let stat: Awaited<ReturnType<typeof fs.stat>>;
      try {
        stat = await fs.stat(fullPath);
      } catch {
        if (countWarnings) warningCount += 1;
        continue;
      }
      const outsideScanWindow = floor === 0
        ? stat.mtimeMs < cutoff
        : stat.mtimeMs <= cutoff;
      if (!stat.isFile() || outsideScanWindow) continue;

      const sessionId = fileDirent.name.slice(0, -'.jsonl'.length);
      let project = '';
      if (countWarnings) {
        // Historical inventory must account for bad provider files before
        // upload filters run. Live scans retain lazy cwd resolution so an
        // unchanged transcript is never opened unnecessarily.
        try {
          project = await resolveSessionCwd(fullPath, sessionId, getHistoryIndex) ?? '';
        } catch {
          warningCount += 1;
          continue;
        }
        if (!project) {
          warningCount += 1;
          continue;
        }
      }

      entries.push({
        sessionId,
        project, // live scans resolve lazily in collectLocalFiles (after cursor check)
        timestamp: stat.mtimeMs,
        resolvedTranscriptPath: fullPath,
      });
    }
  }

  return { entries: entries.sort((a, b) => a.timestamp - b.timestamp), warningCount };
}

// Lazily-built sessionId -> cwd index from history.jsonl, used only as a
// fallback when a transcript has no embedded cwd. history.jsonl is otherwise
// no longer the enumeration source. First occurrence per session wins.
async function buildHistoryCwdIndex(): Promise<Map<string, string>> {
  const index = new Map<string, string>();
  let contents: string;
  try {
    contents = await fs.readFile(claudeHistoryFilePath(), 'utf8');
  } catch {
    return index;
  }
  for (const entry of parseAllHistoryEntries(contents)) {
    const sessionId = entry.sessionId?.trim();
    const project = entry.project?.trim();
    if (sessionId && project && !index.has(sessionId)) index.set(sessionId, project);
  }
  return index;
}

// Resolve a session's cwd: prefer the transcript's own embedded cwd, fall back
// to the history index, else null (caller skips — not an attributable session).
async function resolveSessionCwd(
  transcriptPath: string,
  sessionId: string,
  getHistoryIndex: () => Promise<Map<string, string>>,
): Promise<string | null> {
  const fromTranscript = await readTranscriptCwd(transcriptPath);
  if (fromTranscript) return fromTranscript;
  const index = await getHistoryIndex();
  return index.get(sessionId) ?? null;
}

// Read every entry in ~/.claude/history.jsonl regardless of upload watermark.
// Used by `lore export`, which always uploads the requested session even if
// the daemon already covered it (the API dedupes on the server side).
export async function readAllEntries(): Promise<ClaudeHistoryEntry[]> {
  const historyPath = claudeHistoryFilePath();
  let contents: string;
  try {
    contents = await fs.readFile(historyPath, 'utf8');
  } catch (error) {
    if (isErrorOfType(error, 'ENOENT')) return [];
    throw error;
  }
  return parseAllHistoryEntries(contents, historyPath);
}

// Pick the history entry that an explicit `lore export` invocation should
// upload. When sessionId is provided we walk newest-first and take the first
// matching record. Otherwise we take the most recent entry whose project
// matches `project` exactly or shares its git root, mirroring the legacy
// `tanagram lore export` selector.
export async function resolveExportEntry(opts: {
  sessionId: string;
  project: string;
}): Promise<ClaudeHistoryEntry> {
  const entries = await readAllEntries();
  const sessionId = opts.sessionId.trim();
  if (sessionId) {
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const entry = entries[i];
      if (entry?.sessionId?.trim() === sessionId) return entry;
    }
    // history.jsonl miss: Claude Code only appends rows on certain prompt
    // events (slash commands with `display`, pasted contents), so a session
    // can have a fully populated transcript on disk while history.jsonl
    // knows nothing about it. Scan ~/.claude/projects/* for the transcript
    // before giving up — this is the dominant path for `/share` calls on
    // freshly-started sessions.
    const transcript = await findSessionTranscriptOnDisk(sessionId);
    if (transcript) {
      const projectHint = opts.project.trim() || process.cwd();
      return {
        sessionId,
        project: projectHint,
        timestamp: transcript.mtimeMs,
        resolvedTranscriptPath: transcript.fullPath,
      };
    }
    throw new CLIError(
      `no history entry or transcript file found for session-id "${sessionId}" — check ~/.claude/history.jsonl and ~/.claude/projects/`,
    );
  }

  const cleanProject = path.normalize(opts.project.trim());
  if (!cleanProject) {
    throw new CLIError('export requires either --session-id or a project path');
  }

  const projectGitRoot = await findGitRoot(cleanProject);

  const sorted = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  for (const entry of sorted) {
    if (!entry.sessionId?.trim()) continue;
    const entryProject = path.normalize(entry.project.trim());
    if (!entryProject || entryProject === '.') continue;
    if (entryProject === cleanProject) return entry;
    if (!projectGitRoot) continue;
    const entryGitRoot = await findGitRoot(entryProject);
    if (entryGitRoot && entryGitRoot === projectGitRoot) return entry;
  }

  throw new CLIError(
    `no history entry found for project "${cleanProject}" — pass --session-id to pick a specific session, or cd into the repo where the Claude Code session ran`,
  );
}

// Locate a session transcript on disk by scanning every project subdir
// under ~/.claude/projects/. Used as a fallback when the user passes an
// explicit --session-id that isn't represented in history.jsonl. Returns
// null when ~/.claude/projects/ doesn't exist or no matching file is found.
async function findSessionTranscriptOnDisk(
  sessionId: string,
): Promise<{ fullPath: string; mtimeMs: number } | null> {
  const cleanSessionId = sessionId.trim();
  if (!cleanSessionId) return null;

  const projectsDir = claudeProjectsDir();
  let dirEntries: Dirent[];
  try {
    dirEntries = await fs.readdir(projectsDir, { withFileTypes: true });
  } catch (error) {
    if (isErrorOfType(error, 'ENOENT')) return null;
    throw new Error(`readdir ${projectsDir}: ${String(error)}`);
  }

  for (const dirent of dirEntries) {
    // Skip regular files / symlinks / sockets that may sit alongside the
    // per-project directories. Without this, joining the entry into a
    // path and stat-ing it would throw ENOTDIR on the first non-directory
    // sibling and crash the whole export.
    if (!dirent.isDirectory()) continue;
    const fullPath = path.join(projectsDir, dirent.name, `${cleanSessionId}.jsonl`);
    try {
      const stat = await fs.stat(fullPath);
      if (stat.isFile()) return { fullPath, mtimeMs: stat.mtimeMs };
    } catch (error) {
      // ENOENT: this dir doesn't have a transcript for this session-id.
      // ENOTDIR: TOCTOU race — the readdir snapshot saw a directory but
      // it was replaced with a regular file before this stat() ran. Both
      // are "this sibling isn't a match, keep walking", not crash-worthy.
      if (!isErrorOfType(error, 'ENOENT') && !isErrorOfType(error, 'ENOTDIR')) {
        throw new Error(`stat ${fullPath}: ${String(error)}`);
      }
    }
  }

  return null;
}

export async function resolveClaudeSessionPath(
  entry: ClaudeHistoryEntry,
): Promise<SessionPathResult> {
  const result = await claudeSessionJSONLPath(entry);
  if (result === missingSessionID) {
    throw new Error('history entry is missing sessionId');
  }
  return result;
}

export type ClaudeSessionPathResult = SessionPathResult;

async function collectLocalFiles(
  entries: ClaudeHistoryEntry[],
  options: CollectLocalFilesOptions = {},
): Promise<CollectLocalFilesResult<ClaudeHistoryEntry>> {
  const localFiles: LocalThreadFile[] = [];
  const entryFileKeys = new Array<string>(entries.length);
  const skippedFileKeys = new Set<string>();
  const skippedOutcomes = new Map<string, 'missing' | 'filtered' | 'unchanged'>();
  const deferredFileKeys = new Set<string>();
  const largeDeferredFileKeys = new Set<string>();
  const seenFileKeys = new Set<string>();
  const createdTempPaths: string[] = [];

  // Built once, only if some transcript lacks an embedded cwd.
  let historyIndex: Map<string, string> | null = null;
  const getHistoryIndex = async (): Promise<Map<string, string>> => {
    historyIndex ??= await buildHistoryCwdIndex();
    return historyIndex;
  };

  try {
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const pathResult = await claudeSessionJSONLPath(entry);
      if (pathResult === missingSessionID) {
        const syntheticKey = `__missing_session__:${entry.project}:${entry.timestamp}:${i}`;
        entryFileKeys[i] = syntheticKey;
        skippedFileKeys.add(syntheticKey);
        skippedOutcomes.set(syntheticKey, 'missing');
        seenFileKeys.add(syntheticKey);
        continue;
      }

      entryFileKeys[i] = pathResult.key;
      if (seenFileKeys.has(pathResult.key)) continue;

      const watchedState = await watchedFileStateForPath(pathResult.fullPath);
      if (!watchedState.exists) {
        skippedFileKeys.add(pathResult.key);
        skippedOutcomes.set(pathResult.key, 'missing');
        seenFileKeys.add(pathResult.key);
        continue;
      }

      // Per-session cursor says this transcript is byte-for-byte unchanged
      // since its last upload — skip snapshotting it (still counts as handled).
      if (options.isUnchanged?.(pathResult.key, watchedState)) {
        skippedFileKeys.add(pathResult.key);
        skippedOutcomes.set(pathResult.key, 'unchanged');
        seenFileKeys.add(pathResult.key);
        continue;
      }

      // Changed since last upload, but uploaded too recently — defer this pass
      // (TAN-4540). Deferred (not handled): cursor + floor stay put so the
      // daemon re-uploads once the throttle window elapses.
      if (options.isThrottled?.(pathResult.key)) {
        deferredFileKeys.add(pathResult.key);
        seenFileKeys.add(pathResult.key);
        continue;
      }

      const laneIncludes = options.uploadLane?.includes(pathResult.key, watchedState) ?? true;
      if (!laneIncludes && options.uploadLane?.name === 'normal') {
        largeDeferredFileKeys.add(pathResult.key);
        seenFileKeys.add(pathResult.key);
        continue;
      }

      if (!laneIncludes) {
        seenFileKeys.add(pathResult.key);
        continue;
      }

      // Resolve cwd now (after the cursor skip) — from the transcript itself,
      // then the history index. A file we can't attribute to a cwd (e.g. a
      // non-session .jsonl such as a claude-mem observer log) is skipped.
      const project = entry.project.trim()
        || (await resolveSessionCwd(pathResult.fullPath, entry.sessionId ?? '', getHistoryIndex));
      if (!project) {
        log.info('upload_filter_session_excluded', {
          filename: pathResult.relativeFilename,
          transcript_path: pathResult.fullPath,
          cwd: '<unresolved>',
          reason: 'no_resolvable_cwd',
        });
        skippedFileKeys.add(pathResult.key);
        skippedOutcomes.set(pathResult.key, 'filtered');
        seenFileKeys.add(pathResult.key);
        continue;
      }

      const repoOriginPath = await repoOriginPathForProject(project);
      let transcriptSnapshotContents: string | null = null;
      const readTranscript = async (): Promise<string> => {
        if (transcriptSnapshotContents !== null) return transcriptSnapshotContents;
        transcriptSnapshotContents = await fs.readFile(pathResult.fullPath, 'utf8');
        return transcriptSnapshotContents;
      };

      const filterDecision = await evaluateUploadFilterSession(
        options.uploadFilterConfig ?? emptyUploadFilterConfig(),
        {
          cwd: project,
          repoOriginPath,
          transcriptPath: pathResult.fullPath,
          readTranscript,
        },
      );
      if (!filterDecision.shouldUpload) {
        log.info('upload_filter_session_excluded', {
          filename: pathResult.relativeFilename,
          transcript_path: pathResult.fullPath,
          cwd: project,
          repo_origin_path: repoOriginPath || '<none>',
          reason: filterDecision.reason,
        });
        skippedFileKeys.add(pathResult.key);
        skippedOutcomes.set(pathResult.key, 'filtered');
        seenFileKeys.add(pathResult.key);
        continue;
      }
      log.info('upload_filter_session_included', {
        filename: pathResult.relativeFilename,
        transcript_path: pathResult.fullPath,
        cwd: project,
        repo_origin_path: repoOriginPath || '<none>',
        reason: 'matched',
      });

      let snapshot: { tempPath: string; md5: string };
      try {
        snapshot = await copyFileSnapshot(pathResult.fullPath);
      } catch (error) {
        if (isErrorOfType(error, 'ENOENT')) {
          skippedFileKeys.add(pathResult.key);
          skippedOutcomes.set(pathResult.key, 'missing');
          seenFileKeys.add(pathResult.key);
          continue;
        }
        throw new Error(`read session transcript ${pathResult.fullPath}: ${String(error)}`);
      }
      createdTempPaths.push(snapshot.tempPath);

      localFiles.push({
        key: pathResult.key,
        filename: pathResult.relativeFilename,
        sourcePath: pathResult.fullPath,
        repoOriginPath,
        tempPath: snapshot.tempPath,
        md5: snapshot.md5,
        fileType: 'transcript',
        harness: 'claudeCode',
        harnessInternalId: entry.sessionId?.trim() ?? '',
        watchedState,
      });
      seenFileKeys.add(pathResult.key);

      const transcriptSnapshot = await fs.readFile(snapshot.tempPath, 'utf8');
      const planFilePaths = planFilePathsFromTranscript(transcriptSnapshot);
      for (const planFilePath of planFilePaths) {
        let resolvedPlanFilePath: string;
        try {
          resolvedPlanFilePath = await resolvePlanFilePath(planFilePath);
        } catch {
          continue;
        }

        const planKey = `${pathResult.key}::plan:${resolvedPlanFilePath}`;
        if (seenFileKeys.has(planKey)) continue;

        const planWatchedState = await watchedFileStateForPath(resolvedPlanFilePath);
        let planSnapshot: { tempPath: string; md5: string };
        try {
          planSnapshot = await copyFileSnapshot(resolvedPlanFilePath);
        } catch (error) {
          if (isErrorOfType(error, 'ENOENT')) continue;
          throw new Error(`read plan file ${resolvedPlanFilePath}: ${String(error)}`);
        }
        createdTempPaths.push(planSnapshot.tempPath);

        localFiles.push({
          key: planKey,
          filename: resolvedPlanFilePath,
          sourcePath: resolvedPlanFilePath,
          repoOriginPath,
          tempPath: planSnapshot.tempPath,
          md5: planSnapshot.md5,
          fileType: 'plan',
          harness: 'claudeCode',
          harnessInternalId: resolvedPlanFilePath,
          watchedState: planWatchedState,
        });
        seenFileKeys.add(planKey);
      }
    }
  } catch (error) {
    await Promise.all(
      createdTempPaths.map((tempPath) =>
        fs.rm(path.dirname(tempPath), { recursive: true, force: true }),
      ),
    );
    throw error;
  }

  return { entries, localFiles, entryFileKeys, skippedFileKeys, skippedOutcomes, deferredFileKeys, largeDeferredFileKeys };
}

async function buildPendingTranscriptWatches(
  lastUploadedTimestamp: number,
  baselineOverrides: Map<string, WatchedFileState>,
  options: BuildPendingTranscriptWatchesOptions = {},
): Promise<PendingTranscriptWatch[]> {
  const entries = await readUnuploadedEntries(lastUploadedTimestamp);
  const now = Date.now();
  const watchers: PendingTranscriptWatch[] = [];
  const seenTranscriptPaths = new Set<string>();

  let historyIndex: Map<string, string> | null = null;
  const getHistoryIndex = async (): Promise<Map<string, string>> => {
    historyIndex ??= await buildHistoryCwdIndex();
    return historyIndex;
  };

  for (const entry of entries) {
    const pathResult = await claudeSessionJSONLPath(entry);
    if (pathResult === missingSessionID) continue;
    if (seenTranscriptPaths.has(pathResult.fullPath)) continue;
    seenTranscriptPaths.add(pathResult.fullPath);

    const currentState = await watchedFileStateForPath(pathResult.fullPath);
    if (!currentState.exists) continue;

    const project = entry.project.trim()
      || (await resolveSessionCwd(pathResult.fullPath, entry.sessionId ?? '', getHistoryIndex));
    if (!project) continue;

    const repoOriginPath = await repoOriginPathForProject(project);
    const filterDecision = await evaluateUploadFilterSession(
      options.uploadFilterConfig ?? emptyUploadFilterConfig(),
      {
        cwd: project,
        repoOriginPath,
        transcriptPath: pathResult.fullPath,
        readTranscript: () => fs.readFile(pathResult.fullPath, 'utf8'),
      },
      { ignoreSkillRules: true },
    );
    if (!filterDecision.shouldUpload) {
      log.info('upload_filter_watch_excluded', {
        filename: pathResult.relativeFilename,
        transcript_path: pathResult.fullPath,
        cwd: project,
        repo_origin_path: repoOriginPath || '<none>',
        reason: filterDecision.reason,
      });
      continue;
    }

    watchers.push({
      sessionId: entry.sessionId?.trim() ?? '',
      project,
      transcriptPath: pathResult.fullPath,
      expiresAt: new Date(now + PENDING_WATCH_QUIET_PERIOD_MS),
      baseline: baselineOverrides.get(pathResult.fullPath) ?? currentState,
    });
  }

  return watchers.sort((a, b) => a.transcriptPath.localeCompare(b.transcriptPath));
}

async function claudeSessionJSONLPath(
  entry: ClaudeHistoryEntry,
): Promise<SessionPathResult | typeof missingSessionID> {
  const sessionId = entry.sessionId?.trim();
  if (!sessionId) return missingSessionID;

  const projectsDir = claudeProjectsDir();

  // When the export-entry resolver already located the transcript on
  // disk, trust that path. Re-deriving the encoded dir name from
  // `entry.project` would lose the file when Claude Code's encoder
  // diverges from ours (e.g. dotted path components).
  const preResolved = entry.resolvedTranscriptPath?.trim();
  if (preResolved) {
    const relativeFilename = path
      .relative(projectsDir, preResolved)
      .split(path.sep)
      .join('/');
    return { fullPath: preResolved, relativeFilename, key: relativeFilename };
  }

  const projectPaths = await claudeSessionProjectPaths(entry.project);
  let fallback: SessionPathResult | null = null;

  for (const projectPath of projectPaths) {
    const projectDirName = claudeProjectDirName(projectPath);
    const relativeFilename = path.posix.join(projectDirName, `${sessionId}.jsonl`);
    const fullPath = path.join(projectsDir, ...relativeFilename.split('/'));
    const result = { fullPath, relativeFilename, key: relativeFilename };
    fallback ??= result;

    try {
      await fs.stat(fullPath);
      return result;
    } catch (error) {
      if (!isErrorOfType(error, 'ENOENT')) {
        throw new Error(`stat session transcript ${fullPath}: ${String(error)}`);
      }
    }
  }

  if (!fallback) throw new Error('history entry is missing project path');
  return fallback;
}

async function claudeSessionProjectPaths(project: string): Promise<string[]> {
  let cleanProject = path.normalize(project.trim());
  if (!cleanProject || cleanProject === '.') {
    throw new Error('history entry is missing project path');
  }

  const projectPaths = [cleanProject];
  const gitRoot = await findGitRoot(cleanProject);
  if (!gitRoot || gitRoot === cleanProject) return projectPaths;

  for (let parentDir = path.dirname(cleanProject); parentDir !== cleanProject;) {
    projectPaths.push(parentDir);
    if (parentDir === gitRoot) break;
    cleanProject = parentDir;
    parentDir = path.dirname(parentDir);
  }

  return projectPaths;
}

function claudeProjectDirName(project: string): string {
  const cleanProject = path.normalize(project.trim());
  if (!cleanProject || cleanProject === '.') {
    throw new Error('history entry is missing project path');
  }
  // History entries can carry un-normalized `project` paths, so normalize and
  // validate above before handing the cwd to the shared Claude Code encoder.
  return encodeCwdToDir(cleanProject);
}

function claudeProjectsDir(): string {
  return claudeProjectsRoot();
}

function claudePlansDir(): string {
  return path.join(os.homedir(), '.claude', 'plans');
}

function planFilePathsFromTranscript(contents: string): string[] {
  const planFilePaths: string[] = [];
  const seen = new Set<string>();

  for (const line of contents.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    let entry: ClaudeTranscriptEntry;
    try {
      entry = JSON.parse(trimmed) as ClaudeTranscriptEntry;
    } catch {
      break;
    }

    if (entry.type !== 'assistant') continue;
    const content = entry.message?.content;
    if (!Array.isArray(content)) continue;
    for (const block of content) {
      if (!isRecord(block)) continue;
      if (block.type !== 'tool_use' || block.name !== 'ExitPlanMode') continue;
      if (!isRecord(block.input)) continue;
      const planFilePath = block.input.planFilePath;
      if (typeof planFilePath !== 'string') continue;
      const normalized = planFilePath.trim();
      if (!normalized || seen.has(normalized)) continue;
      planFilePaths.push(normalized);
      seen.add(normalized);
    }
  }

  return planFilePaths;
}

async function resolvePlanFilePath(planFilePath: string): Promise<string> {
  let cleanPlanFilePath = planFilePath.trim();
  if (!cleanPlanFilePath) throw new Error('plan file path is empty');

  const plansDir = claudePlansDir();
  let resolvedPlansDir = plansDir;
  try {
    resolvedPlansDir = await fs.realpath(plansDir);
  } catch {
    // If the plans dir does not exist yet, still validate lexical containment.
  }

  if (cleanPlanFilePath.startsWith('~/')) {
    cleanPlanFilePath = path.join(os.homedir(), cleanPlanFilePath.slice(2));
  }
  if (!path.isAbsolute(cleanPlanFilePath)) {
    throw new Error(`plan file path must stay under ${plansDir}`);
  }

  cleanPlanFilePath = path.normalize(cleanPlanFilePath);
  if (!pathWithinDir(cleanPlanFilePath, plansDir)) {
    throw new Error(`plan file path must stay under ${plansDir}`);
  }

  try {
    const resolvedPlanFilePath = await fs.realpath(cleanPlanFilePath);
    if (!pathWithinDir(resolvedPlanFilePath, resolvedPlansDir)) {
      throw new Error(`plan file path must resolve under ${plansDir}`);
    }
  } catch (error) {
    if (!isErrorOfType(error, 'ENOENT')) throw error;
  }

  return cleanPlanFilePath;
}

function pathWithinDir(filePath: string, dir: string): boolean {
  const rel = path.relative(dir, filePath);
  return rel === '' || (rel !== '..' && !rel.startsWith(`..${path.sep}`));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
